import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Dimmer,
  Grid,
  Input,
  Select,
  Loader,
  Segment,
  Icon,
} from 'semantic-ui-react';
import DOCUMENT_TYPE_OPTIONS from '../../../constants/documentType';
import { namespaces, TRANSLATION_FILES } from '../../../constants/translation';

class UploadSharedMedia extends Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    t: PropTypes.func,
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      uploads: [],
      names: [],
      types: [],
      hasChanges: false,
    };
  }

  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => res(loading));
    });
  }

  getIconName = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();

    if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tif', 'tiff'].includes(ext)) {
      return 'file image outline';
    }

    if (['doc', 'docx', 'rtf', 'odt'].includes(ext)) {
      return 'file word outline';
    }

    if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
      return 'file excel outline';
    }

    if (['ppt', 'pptx'].includes(ext)) {
      return 'file powerpoint outline';
    }

    if (ext === 'txt') {
      return 'file text outline';
    }

    if (ext === 'pdf') {
      return 'file pdf outline';
    }

    return 'file alternate outline';
  }

  handleSubmit = (event) => {
    event.preventDefault();
    if (this.state.uploads.length > 0) {
      // Package files and names together for the parent component to process
      const payload = this.state.uploads.map((upload, idx) => ({
        file: upload.file,
        fileName: this.state.names[idx] || upload.file.name,
        fileType: this.state.types[idx],
      }));

      if (this.props.onSubmit) {
        this.props.onSubmit(event, { files: payload });
      }
    }
  }

  handleCheckChanges() {
    if (!this.state.hasChanges) {
      this.setState({ hasChanges: true }, () => {
        if (this.props.onChange) this.props.onChange(true);
      });
    }
  }

  handleOnChange = () => {
    const files = Array.prototype.slice.call(this.uploadFiles.files);
    this.uploadFiles.value = null;

    files.forEach((file) => {
      const lastDot = file.name.lastIndexOf('.');
      const cleanName = lastDot > 0 ? file.name.substring(0, lastDot) : file.name;

      this.setState(prev => ({
        uploads: [...prev.uploads, { file, name: file.name }],
        names: [...prev.names, cleanName],
        types: [...prev.types, 'other_documents'],
      }), () => {
        this.handleCheckChanges();
      });
    });
  }
  handleRemove = (event, idx) => {
    event.preventDefault();
    this.setState(prev => ({
      uploads: prev.uploads.filter((_, i) => i !== idx),
      names: prev.names.filter((_, i) => i !== idx),
      types: prev.types.filter((_, i) => i !== idx),
    }));
  }

  handleInputChange = (e, { value }, idx) => {
    this.setState((prev) => {
      const names = [...prev.names];
      names[idx] = value;
      return { names };
    });
  }

  handleTypeChange = (e, { value }, idx) => {
    this.setState((prev) => {
      const types = [...prev.types];
      types[idx] = value;
      return { types };
    });
  }
  render() {
    const { uploads, names, loading } = this.state;
    const { t } = this.props;
    const translatedOptions = DOCUMENT_TYPE_OPTIONS.map(option => ({
      ...option,
      text: t(option.text, { ns: TRANSLATION_FILES.translation }),
    }));
    return (
      <form
        encType="multipart/form-data"
        onSubmit={this.handleSubmit}
        ref={(ref) => { this.form = ref; }}
      >
        {/* Input expanded to match your Go backend AllowedContentTypes */}
        <input
          ref={(ref) => { this.uploadFiles = ref; }}
          type="file"
          onChange={this.handleOnChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.rtf,.odt,.ods,.txt,.csv,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tif,.tiff"
          multiple
          style={{ display: 'none' }}
        />

        <Grid verticalAlign="middle" stackable>
          <Grid.Row columns="equal">
            <Grid.Column>
              <Button
                fluid
                compact
                primary
                type="button"
                icon="add"
                content={t('Select Files', { ns: TRANSLATION_FILES.translation })}
                onClick={() => this.uploadFiles.click()}
                disabled={loading}
              />
            </Grid.Column>
            <Grid.Column>
              <Button
                fluid
                compact
                type="submit"
                positive
                content={t('Upload All', { ns: TRANSLATION_FILES.translation })}
                icon="cloud upload"
                loading={loading}
                disabled={uploads.length < 1 || loading}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Grid stackable columns={1} style={{ marginTop: '1em' }}>
          <Dimmer active={loading} inverted style={{ zIndex: 20 }}>
            <Loader>Preparing Uploads</Loader>
          </Dimmer>
          {uploads.map((upload, idx) => (
            <Grid.Column key={idx}>
              <Segment padded secondary>
                <Grid verticalAlign="middle">
                  <Grid.Column width={1}>
                    <Icon name={this.getIconName(upload.file.name)} size="large" />
                  </Grid.Column>
                  <Grid.Column width={7}>
                    <Input
                      placeholder="Display Name"
                      fluid
                      value={names[idx]}
                      onChange={(e, data) => this.handleInputChange(e, data, idx)}
                    />
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Select
                      placeholder="Select Type"
                      fluid
                      selection
                      options={translatedOptions}
                      value={this.state.types[idx]}
                      onChange={(e, data) => this.handleTypeChange(e, data, idx)}
                    />
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <Button
                      type="button"
                      icon="delete"
                      fluid
                      compact
                      color="red"
                      negative
                      onClick={e => this.handleRemove(e, idx)}
                    />
                  </Grid.Column>
                </Grid>
              </Segment>
            </Grid.Column>
          ))}
        </Grid>
      </form>
    );
  }
}

export default withTranslation(namespaces, { withRef: true })(UploadSharedMedia);



// WEBPACK FOOTER //
// ./src/containers/Opportunities/Overview/Form.jsx